// Salidas de productos a otras taquerías (pestaña del cuadre). Separada de Gastos:
// NO es salida de efectivo ni inventario. Cada salida genera una deuda de la taquería destino con esta.
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { listTaquerias, listSalidasTurno, crearSalidaProductos, editarSalidaProductos, anularSalidaProductos, subirComprobante } from '../../services/data';
import { AdjuntarComprobante } from '../finanzas/Comprobante';
import { toMoney, sumarDinero } from '../../utils/numbers';
import { formatDateTime } from '../../utils/dates';
import ProductoAutocomplete from './ProductoAutocomplete';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import MoneyInput from '../common/MoneyInput';

const n = (v) => Number(v) || 0;
const cantOk = (v) => /^\d*(?:\.\d{0,3})?$/.test(v);
let seq = 0; const uid = () => `s${Date.now()}${seq++}`;
// Por producto: cantidad, descripción libre y valor total OPCIONAL. Vacío = "Valor pendiente" (no Q0.00); lo completa Administración.
const nuevoItem = () => ({ key: uid(), producto: '', productoId: null, cantidad: '', detalleCantidad: '', total: '' });
const nuevaSalida = (entregadoPor = '') => ({ key: uid(), destinoId: '', entregadoPor, recibidoPor: '', reciboArchivo: null, reciboUrl: '', observacion: '', items: [nuevoItem()], editando: true });
const desdeServidor = (s) => ({ ...s, key: s.id, editando: false,
  items: s.items.map((i) => ({ key: uid(), producto: i.producto, productoId: i.productoId || null, cantidad: String(i.cantidad),
    detalleCantidad: i.detalleCantidad || '', total: i.total == null ? '' : String(i.total) })) });
const pendiente = (i) => String(i.total ?? '').trim() === '';
const totalSalida = (s) => sumarDinero(...s.items.filter((i) => !pendiente(i)).map((i) => i.total)); // solo lo valorado
const pendientesDe = (s) => s.items.filter(pendiente).length;
const fechaHora = (iso) => iso ? formatDateTime(iso) : '';

/** aperturaId: turno del cuadre; origenId: sucursal que entrega; editable: turno abierto o en corrección. */
export default function SalidasProductos({ aperturaId, origenId, editable, onCount, onSinGuardar, children }) {
  const [taquerias, setTaquerias] = useState([]);
  const [salidas, setSalidas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busy, setBusy] = useState(false);
  const { me } = useSucursalActiva(); // "Entregó" se sugiere con el usuario actual, pero se puede cambiar

  const cargar = useCallback(async () => {
    if (!aperturaId) { setCargando(false); return; }
    try {
      const [t, lista] = await Promise.all([listTaquerias(), listSalidasTurno(aperturaId)]);
      setTaquerias(t);
      setSalidas(lista.map(desdeServidor));
    } catch (e) { Swal.fire('Error', e.message || 'No se pudieron cargar las salidas de productos.', 'error'); }
    finally { setCargando(false); }
  }, [aperturaId]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { onCount?.(salidas.filter((s) => s.id && s.estado !== 'Anulada').length); }, [salidas, onCount]);
  useEffect(() => { onSinGuardar?.(salidas.filter((s) => !s.id).length); }, [salidas, onSinGuardar]);

  const destinos = taquerias.filter((t) => t.id !== origenId);
  const nombre = (id) => taquerias.find((t) => t.id === id)?.nombre || '';
  const set = (key, patch) => setSalidas((ss) => ss.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const setItem = (key, ik, patch) => setSalidas((ss) => ss.map((s) => (s.key !== key ? s : { ...s, items: s.items.map((i) => (i.key === ik ? { ...i, ...patch } : i)) })));

  const guardar = async (s) => {
    if (!s.destinoId) return Swal.fire('Revisa la salida', 'Selecciona la taquería destino.', 'warning');
    if (!s.entregadoPor?.trim()) return Swal.fire('Revisa la salida', 'Indica quién entregó los productos.', 'warning');
    if (!s.recibidoPor?.trim()) return Swal.fire('Revisa la salida', 'Indica quién recibió los productos en la taquería destino.', 'warning');
    if (!s.items.length || s.items.some((i) => !i.producto.trim() || !(n(i.cantidad) > 0)))
      return Swal.fire('Revisa la salida', 'Completa producto y cantidad de cada línea. El valor total es opcional.', 'warning');
    setBusy(true);
    try {
      // Mismo almacenamiento que los demás comprobantes: se sube al guardar la salida.
      const reciboUrl = s.reciboArchivo
        ? await subirComprobante(s.reciboArchivo, (s.reciboArchivo.name || 'recibo-firmado').replace(/[^\w.-]+/g, '_'), s.reciboArchivo.type)
        : s.reciboUrl;
      const payload = { aperturaId, destinoId: s.destinoId, entregadoPor: s.entregadoPor, recibidoPor: s.recibidoPor, reciboUrl, observacion: s.observacion,
        items: s.items.map((i) => ({ producto: i.producto, productoId: i.productoId || null, cantidad: i.cantidad, detalleCantidad: i.detalleCantidad,
          total: pendiente(i) ? '' : i.total })) }; // vacío viaja vacío: el servidor lo guarda como valor pendiente
      const r = s.id ? await editarSalidaProductos(s.id, payload) : await crearSalidaProductos(payload);
      setSalidas((ss) => ss.map((x) => (x.key === s.key ? desdeServidor(r) : x)));
      Swal.fire({ icon: 'success', title: s.id ? 'Salida actualizada' : 'Salida registrada', timer: 2400, showConfirmButton: false,
        text: r.valorPendiente ? `${r.productosPendientes} producto(s) con valor pendiente: Administración completará el valor.` : `${r.destino} debe ${toMoney(r.total)} a esta taquería.` });
    } catch (e) { Swal.fire('No se pudo guardar', e.message || 'Intenta de nuevo.', 'error'); }
    finally { setBusy(false); }
  };

  const anular = async (s) => {
    const { value: motivo } = await Swal.fire({ title: 'Anular salida de productos', input: 'textarea', inputLabel: 'Motivo de la anulación',
      text: `La deuda de ${s.destino} por ${toMoney(s.total)} quedará anulada.`, showCancelButton: true, confirmButtonText: 'Anular salida',
      cancelButtonText: 'Cancelar', inputValidator: (v) => (!v?.trim() ? 'Indica el motivo' : undefined) });
    if (!motivo) return;
    setBusy(true);
    try {
      const r = await anularSalidaProductos(s.id, motivo.trim());
      setSalidas((ss) => ss.map((x) => (x.key === s.key ? desdeServidor(r) : x)));
    } catch (e) { Swal.fire('No se pudo anular', e.message || '', 'error'); }
    finally { setBusy(false); }
  };

  const cancelar = (s) => {
    if (!s.id) return setSalidas((ss) => ss.filter((x) => x.key !== s.key));
    return cargar();
  };

  if (cargando) return <p className="ct-card ct-muted">Cargando salidas de productos…</p>;
  const vigentes = salidas.filter((s) => s.id && s.estado !== 'Anulada');

  return (
    <section role="tabpanel" className="ct-lista">
      {children}
      <p className="ct-nota ct-salidas-aviso">Registra los productos que esta taquería entrega a otra. No sale efectivo de la caja ni es un gasto:
        genera una cuenta por cobrar a la taquería destino.</p>
      {!salidas.length && !editable && <p className="ct-card ct-muted">Sin salidas de productos en este turno.</p>}
      {salidas.map((s, si) => {
        // Estado por salida (mismo patrón que Compras y Gastos): una salida guardada (con id) está COLAPSADA;
        // se EXPANDE si es nueva (sin id), si el usuario tocó Editar (`editando`) o "Ver" (`verDetalle`, solo lectura).
        const abierta = editable && s.editando;
        const anulada = s.estado === 'Anulada';
        const modificable = editable && !anulada && s.pagado <= 0; // con pagos o anulada no se edita ni anula (reglas existentes)
        const expandida = !s.id || s.editando || s.verDetalle;
        const etiqueta = anulada ? 'Anulada' : s.pagado > 0 ? s.estadoCuenta : s.valorPendiente ? 'Valor pendiente' : '';
        if (!expandida) return (
          <article className={`ct-card ct-compra-fila${anulada ? ' ct-salida--anulada' : ''}`} key={s.key}>
            <span className="ct-fila-titulo"><b>{s.destino || nombre(s.destinoId)} · {s.valorPendiente && !s.total ? 'Valor pendiente' : toMoney(s.total)}</b>
              {etiqueta && !(s.valorPendiente && !s.total) && <small> · {etiqueta}</small>}</span>
            <span className="ct-salida-acciones">
              {modificable ? <>
                <button type="button" className="ct-link" disabled={busy} onClick={() => set(s.key, { editando: true })}>Editar</button>
                <button type="button" className="ct-link ct-danger" disabled={busy} onClick={() => anular(s)}>Anular</button>
              </> : <button type="button" className="ct-link" onClick={() => set(s.key, { verDetalle: true })}>Ver</button>}
            </span>
          </article>
        );
        return (
          <article className={`ct-card${anulada ? ' ct-salida--anulada' : ''}`} key={s.key}>
            <div className="ct-card-head">
              <h3>Salida {si + 1}{s.id && <small> · {fechaHora(s.fechaAt)} · {s.usuario}</small>}
                {etiqueta && <small> · {etiqueta}{s.pagado > 0 && !anulada ? ` (${toMoney(s.pagado)} pagado)` : ''}</small>}</h3>
              {/* Quitar (nueva) o Cancelar (en edición) arriba a la derecha, como en Compras y Gastos. */}
              {abierta ? <button className={s.id ? 'ct-link' : 'ct-link ct-danger'} disabled={busy} onClick={() => cancelar(s)}>{s.id ? 'Cancelar' : 'Quitar'}</button>
                : s.verDetalle && <button className="ct-link" onClick={() => set(s.key, { verDetalle: false })}>Ocultar</button>}
            </div>
            <div className="ct-form">
              <label className="ct-span2">Taquería destino<select value={s.destinoId} disabled={!abierta} onChange={(e) => set(s.key, { destinoId: e.target.value })}>
                <option value="">Selecciona</option>
                {destinos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                {s.destinoId && !destinos.some((t) => t.id === s.destinoId) && <option value={s.destinoId}>{s.destino || nombre(s.destinoId)}</option>}
              </select></label>
              {/* Entregó | Recibido por en una fila, como los pares de Compras. */}
              <div className="ct-span2 ct-par">
              <label>Entregó<input value={s.entregadoPor} disabled={!abierta} maxLength={120} placeholder="Quién entrega en esta taquería"
                onChange={(e) => set(s.key, { entregadoPor: e.target.value })} /></label>
              <label>Recibido por<input value={s.recibidoPor} disabled={!abierta} maxLength={120} placeholder="Quién recibe en la taquería destino"
                onChange={(e) => set(s.key, { recibidoPor: e.target.value })} /></label>
              </div>
              <label className="ct-span2">Observación (opcional)<input value={s.observacion} disabled={!abierta} maxLength={500} onChange={(e) => set(s.key, { observacion: e.target.value })} /></label>
              <div className="ct-span2 ct-adjunto-ancho"><span className="ct-campo-titulo">Recibo firmado</span>
                {!abierta && !s.reciboUrl ? <small className="ct-muted">Sin comprobante</small>
                  : <AdjuntarComprobante archivo={s.reciboArchivo} url={s.reciboUrl} disabled={!abierta} ayuda="Recibo de entrega firmado."
                    onChange={(f) => set(s.key, f ? { reciboArchivo: f } : { reciboArchivo: null, reciboUrl: '' })} />}
                {abierta && <small className="ct-muted">Opcional: recibo firmado por quien recibió los productos.</small>}</div>
            </div>
            {/* Mismo patrón que Compras: Producto · Cantidad | Valor total (opcional) · Descripción. El valor unitario lo calcula el sistema. */}
            <table className="ct-tabla ct-tabla--productos ct-tabla--compra">
              <thead><tr><th>Producto</th><th className="num">Cantidad</th><th className="num">Valor total (opcional)</th><th>Descripción</th><th /></tr></thead>
              <tbody>{s.items.map((it) => (
                <tr key={it.key}>
                  <td data-label="Producto"><ProductoAutocomplete value={it.producto} productoId={it.productoId} disabled={!abierta} onChange={(v) => setItem(s.key, it.key, v)} /></td>
                  <td data-label="Cantidad" className="num"><input inputMode="decimal" placeholder="0" value={it.cantidad} disabled={!abierta} aria-label="Cantidad"
                    onChange={(e) => cantOk(e.target.value) && setItem(s.key, it.key, { cantidad: e.target.value })} /></td>
                  <td data-label="Valor total" className="num">{!abierta && pendiente(it) ? <span className="ct-valor-pendiente">Valor pendiente</span>
                    : <MoneyInput className="mi--tabla" value={it.total} disabled={!abierta} aria-label="Valor total (opcional; vacío = valor pendiente)"
                      onChange={(v) => setItem(s.key, it.key, { total: v })} />}
                    {abierta && pendiente(it) && <small className="ct-valor-pendiente">Valor pendiente</small>}</td>
                  <td data-label="Descripción"><input value={it.detalleCantidad} disabled={!abierta} maxLength={60} placeholder="Ej.: 10 limones" aria-label="Descripción"
                    onChange={(e) => setItem(s.key, it.key, { detalleCantidad: e.target.value })} /></td>
                  <td>{abierta && s.items.length > 1 && <button className="ct-link ct-danger" aria-label="Quitar producto"
                    onClick={() => set(s.key, { items: s.items.filter((x) => x.key !== it.key) })}>×</button>}</td>
                </tr>))}
              </tbody>
            </table>
            <div className="ct-card-foot">
              {abierta ? <button className="ct-btn" onClick={() => set(s.key, { items: [...s.items, nuevoItem()] })}>+ Agregar producto</button> : <span />}
              <div className="ct-total ct-total--inline">Total entregado <strong>{toMoney(totalSalida(s))}</strong>
                {pendientesDe(s) > 0 && <small className="ct-valor-pendiente"> + {pendientesDe(s)} con valor pendiente</small>}</div>
            </div>
            {s.id && anulada && s.anuladoMotivo && <p className="ct-muted">Anulada por {s.anuladoPor}: {s.anuladoMotivo}</p>}
            {abierta && <button type="button" className="ct-btn ct-btn--primary" disabled={busy} onClick={() => guardar(s)}>{s.id ? 'Guardar cambios' : 'Guardar salida'}</button>}
          </article>
        );
      })}
      {vigentes.length > 0 && <div className="ct-total">Total salidas de productos <strong>{toMoney(vigentes.reduce((t, s) => t + s.total, 0))}</strong></div>}
      {editable && <button className="ct-btn ct-btn--primary" onClick={() => setSalidas((ss) => [...ss, nuevaSalida(me?.username || '')])}>+ Agregar salida de productos</button>}
    </section>
  );
}
